import { useMemo, useState } from 'react';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { useAuthContext } from '@/auth/useAuthContext';
import { useIsAnnouncementBlocking } from '@/components/announcements';
import { AVISO, useAvisoUnico } from '@/components/aviso-unico';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { PATH_DASHBOARD } from '@/routes/paths';
import { erroNaTela, getNotasAvulsas, NOTA_AVULSA_STATUS } from '@/services/nfseAvulsa';

import { caminhoParaCorrigir } from '../../functions/notaAvulsa';
import { RecusaNotaAvulsaDialog } from './RecusaNotaAvulsaDialog';
import { useIrParaPassoDoCadastro } from './useIrParaPassoDoCadastro';

export const NFSE_AVULSA_RECUSADAS_KEY = ['nfse-avulsa-recusadas'];

export const recusaVistaKey = (userId) => `cakto:nfse-avulsa:recusas-vistas:${userId}`;

// Teto do que fica guardado: o suficiente para o histórico de recusas de qualquer conta, sem o
// storage crescer para sempre.
const MAX_VISTAS = 200;

// As mesmas telas de entrada do aviso de impedimento de NFS-e: onde se chega para olhar, e não
// para preencher. Aberto sobre um formulário, o modal prende o foco e engole o que se digita.
const ENTRY_PATHS = [
  PATH_DASHBOARD.general.homeV2,
  PATH_DASHBOARD.general.home,
  PATH_DASHBOARD.general.products.root,
  PATH_DASHBOARD.general.mySales,
  PATH_DASHBOARD.fiscal.financialInvoices,
];

const isEntryPath = (pathname) =>
  ENTRY_PATHS.some((path) => matchPath({ path, end: true }, pathname));

const STORAGES = [() => window.localStorage, () => window.sessionStorage];

/**
 * Os ids das notas recusadas que a pessoa já viu, ou `null` quando nenhum storage responde.
 *
 * Sem storage, a nota conta como vista: o aviso abriria a cada carregamento, para sempre, e a
 * recusa continua na lista com o chip "Erro". Insistir é o erro mais caro aqui.
 */
const lerVistas = (storageKey) => {
  let legivel = false;
  const vistas = new Set();

  STORAGES.forEach((getStorage) => {
    try {
      const salvo = JSON.parse(getStorage().getItem(storageKey) || '[]');
      legivel = true;
      if (Array.isArray(salvo)) salvo.forEach((id) => vistas.add(String(id)));
    } catch {
      // Ilegível: o outro storage ainda pode responder.
    }
  });

  return legivel ? vistas : null;
};

const gravarVistas = (storageKey, vistas) => {
  const lista = JSON.stringify([...vistas].slice(-MAX_VISTAS));

  STORAGES.some((getStorage) => {
    try {
      getStorage().setItem(storageKey, lista);
      return true;
    } catch {
      return false;
    }
  });
};

/**
 * O aviso de nota avulsa recusada ao abrir o painel (T8, RN06).
 *
 * No máximo uma vez por nota: fechar, corrigir ou clicar fora marca como vista todas as recusadas
 * da lista, e o modal cita as outras em vez de abrir uma atrás da outra. Só nas telas de entrada,
 * e nunca por cima do modal de novidades.
 */
export function RecusaNotaAvulsaAviso() {
  const navigate = useNavigate();
  const irParaPasso = useIrParaPassoDoCadastro();
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  // A área fiscal fecha para quem está impersonando, e a recusa vista pela equipe não foi vista
  // pelo produtor.
  const { impersonatedUserId } = useImpersonation();
  const isAnnouncementBlocking = useIsAnnouncementBlocking();
  const [vistasAqui, setVistasAqui] = useState(() => new Set());

  const storageKey = user?.id && !impersonatedUserId ? recusaVistaKey(user.id) : null;
  const naEntrada = isEntryPath(pathname);

  // Sem a avulsa liberada (`nfseAvulsaEnabled`, a mesma regra da trava do painel), não há
  // recusa a avisar e a consulta nem sai: fora do piloto ela voltaria 403 em toda entrada.
  const consultar = Boolean(
    storageKey && user?.nfseEnabled && user?.nfseAvulsaEnabled && naEntrada
  );

  const { data, isFetched } = useQuery({
    queryKey: NFSE_AVULSA_RECUSADAS_KEY,
    onError: erroNaTela,
    queryFn: ({ signal }) => getNotasAvulsas({ status: NOTA_AVULSA_STATUS.erro }, { signal }),
    // Fora do piloto (403) ou sem a NFS-e liberada, não há o que avisar: silêncio.
    enabled: consultar,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const naoVistas = useMemo(() => {
    if (!storageKey) return [];

    const vistas = lerVistas(storageKey);
    if (!vistas) return [];

    const recusadas = (data?.results ?? []).filter(
      (nota) => nota?.status === NOTA_AVULSA_STATUS.erro
    );

    return recusadas.filter(
      (nota) => !vistas.has(String(nota.id)) && !vistasAqui.has(String(nota.id))
    );
  }, [data, storageKey, vistasAqui]);

  const marcarVistas = () => {
    const ids = naoVistas.map((nota) => String(nota.id));
    const vistas = lerVistas(storageKey) ?? new Set();
    ids.forEach((id) => vistas.add(id));

    gravarVistas(storageKey, vistas);
    setVistasAqui((anteriores) => new Set([...anteriores, ...ids]));
  };

  // Um modal por carregamento: o aviso de impedimento de NFS-e tem a vez antes deste.
  const podeAbrir = useAvisoUnico(AVISO.recusaNotaAvulsa, {
    decidido: !consultar || isFetched,
    querAbrir: naEntrada && !isAnnouncementBlocking && naoVistas.length > 0,
  });

  if (!podeAbrir) return null;

  // A lista vem das mais novas para as mais antigas: a primeira é a recusa mais recente.
  const [nota, ...outras] = naoVistas;

  const irPara = (path) => {
    marcarVistas();
    navigate(path);
  };

  return (
    <RecusaNotaAvulsaDialog
      open
      nota={nota}
      outrasRecusadas={outras.length}
      onClose={marcarVistas}
      onCorrigir={() => irPara(caminhoParaCorrigir(nota.id))}
      onCorrigirCadastro={(chave) => {
        marcarVistas();
        irParaPasso(chave);
      }}
    />
  );
}
